/**
 * AR Vision Demo — demo-only workflow canvas (scenes 5 + 9).
 *
 * THROWAWAY DEMO CODE. Renders the `workflow` slice of the fixture store as a
 * ReactFlow graph that uses the PRODUCT'S REAL node — `StepNode` from
 * `src/lib/steps/step-node.tsx` — so the canvas reads as the actual workflow
 * editor rather than a bespoke mockup. We keep our own thin canvas (not the
 * full WorkflowEditor) so the scripted beats stay under demo control:
 *   - scene 5 (`ar_create_reminder_workflow`) flips `created` true and the base
 *     nodes build themselves in one-by-one (staggered reveal),
 *   - scene 5b (`ar_highlight_statement`) crossfades the statement PDF preview
 *     (shown in the click-to-inspect detail pane on the statement node),
 *   - scene 9 (`ar_amend_workflow`) flips `amended` true and reveals the
 *     `added` branch with the product's real "added" draft treatment.
 *
 * `StepNode` is rendered read-only (`editable: false`, so no delete/duplicate
 * chrome) and wrapped in `ConnectionDragProvider` (a dependency of the node).
 * To read as the real editor, we wrap the canvas in the PRODUCT'S OWN chrome:
 * the real `WorkflowEditorHeader` toolbar (fed demo props + no-op handlers), the
 * real `NodeLibraryContent` catalog (right panel when nothing is selected), and
 * the ReactFlow `MiniMap`/`Controls`. When a node is selected the right panel
 * swaps to our `NodeInspector` (which shows the statement PDF on the statement
 * node) — mirroring the real editor's catalog↔inspector panel. The old fixed
 * "Workflow steps" summary panel is gone — Nami narrates that in chat (scene 5).
 * Delete alongside frontend/src/demo/ar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ReactFlow,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  type Node,
  type Edge,
  type NodeProps,
  type NodeTypes,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { MapTrifoldIcon, SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { IOType } from '@erp/shared-types/node-graph';
import { FLOW_IN_SOCKET_ID, FLOW_OUT_SOCKET_ID } from '@erp/workflows';
import paths from '@/paths';
import { cn } from 'src/lib/utils';
import { StepNode } from 'src/lib/steps/step-node';
import { ConnectionDragProvider } from 'src/pages/authenticated/workflows/context/ConnectionDragContext';
import { WorkflowEditorHeader } from 'src/pages/authenticated/workflows/components/WorkflowEditorHeader';
import { NodeLibraryContent } from 'src/pages/authenticated/workflows/components/NodeLibraryContent';
import { getCategoryStyle } from 'src/lib/workflows/categoryStyles';
import { BRAND } from './fixture-seed';
import { useArDemoStore } from './fixture-store';
import type { DemoWorkflow, DemoWorkflowNode } from './fixture-store.types';
import { useArAgentPage } from './useArAgentPage';
import type { ArAgentTool } from './useArAgentPage';
import { signalWorkflowBuildComplete } from './workflow-build-signal';

const STATEMENT_NODE_ID = 'wf-statement';

// The real toolbar (`WorkflowEditorHeader`) is pure chrome here — its buttons
// are wired to no-ops so the editor reads as real without doing anything.
function noop(): void {}
function noopFile(_file: File): void {}
function noopString(_value: string): void {}
function noopTags(_tags: string[]): void {}

// ---------------------------------------------------------------------------
// Reveal wrapper around the real product node. `StepNode` has no notion of the
// demo's staggered "build itself" animation, so we wrap it in a div that fades
// + scales each node in as it's revealed. All nodes stay mounted (so fitView
// frames the whole graph); only their opacity changes, so the view never jumps.
// ---------------------------------------------------------------------------
function RevealableStepNode(props: NodeProps) {
  const revealed = (props.data as { __revealed?: boolean }).__revealed ?? true;
  return (
    <div
      className={cn(
        'transition-all duration-500 ease-out',
        revealed ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
      )}
    >
      <StepNode {...props} />
    </div>
  );
}

const nodeTypes: NodeTypes = { stepNode: RevealableStepNode };

/** Synthesize the real node's socket map from a list of socket display names. */
function toSocketMap(
  nodeId: string,
  names: string[] | undefined,
  ioType: IOType,
): Record<string, { id: string; ioType: IOType; socketType: string }> {
  const prefix = ioType === IOType.INPUT ? 'in' : 'out';
  const out: Record<
    string,
    { id: string; ioType: IOType; socketType: string }
  > = {};
  for (const name of names ?? []) {
    out[name] = {
      id: `${nodeId}-${prefix}-${name}`,
      ioType,
      socketType: 'json',
    };
  }
  return out;
}

// ---------------------------------------------------------------------------
// Map the fixture workflow slice → ReactFlow nodes/edges shaped for `StepNode`.
// Edges are wired through the product's flow-control sockets so the node's
// flow handles render connected. When the workflow hasn't been amended yet, the
// `added` branch nodes/edges are hidden so scene 9 can reveal them.
// ---------------------------------------------------------------------------
function toGraph(workflow: DemoWorkflow): { nodes: Node[]; edges: Edge[] } {
  const showAdded = workflow.amended;
  const nodes: Node[] = workflow.nodes
    .filter((n) => showAdded || !n.added)
    .map((n) => ({
      id: n.id,
      type: 'stepNode',
      position: n.position,
      data: {
        label: n.title,
        category: n.category,
        stepType: n.stepType,
        editable: false,
        inputs: toSocketMap(n.id, n.sockets?.inputs, IOType.INPUT),
        outputs: toSocketMap(n.id, n.sockets?.outputs, IOType.OUTPUT),
        // Scene-9 nodes reuse the product's real "added" draft treatment.
        __draftState: n.added ? 'added' : undefined,
      },
      draggable: true,
    }));
  const edges: Edge[] = workflow.edges
    .filter((e) => showAdded || !e.added)
    .map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      // Connect through the product's flow-control sockets so the node's flow
      // handles light up as connected (left flow-in ← right flow-out).
      sourceHandle: FLOW_OUT_SOCKET_ID,
      targetHandle: FLOW_IN_SOCKET_ID,
      label: e.label,
      animated: true,
      // Match the editor's default edge: slate bezier; newly-added branch in the
      // same violet as draft "added" edges.
      style: e.added
        ? { stroke: '#8b5cf6', strokeWidth: 2 }
        : { stroke: '#94a3b8', strokeWidth: 2 },
      labelStyle: { fontSize: 11, fontWeight: 600, fill: '#475569' },
      labelBgStyle: { fill: '#f8fafc' },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 4,
    }));
  return { nodes, edges };
}

const selectWorkflow = (s: { workflow: DemoWorkflow }) => s.workflow;

// Stable empty tool set: the create/amend tools are registered globally via
// DemoWorkflowToolsMount; this page only needs a live agent module to exist so
// the Nami chat panel stays enabled/open on this route.
const DEMO_WORKFLOW_NO_TOOLS: ArAgentTool[] = [];

export function DemoWorkflowPage() {
  const workflow = useArDemoStore(selectWorkflow);
  const { nodes, edges } = useMemo(() => toGraph(workflow), [workflow]);
  const gt = useGT();
  const navigate = useNavigate();
  const [isMinimapOpen, setIsMinimapOpen] = useState(true);
  const [catalogSearch, setCatalogSearch] = useState('');

  // Keep Nami registered on this route so the chat panel stays open/usable here
  // (needed for Scene 9 "add that to the workflow"). Without a live agent
  // module the panel is disabled and disappears on navigation.
  useArAgentPage({ pageId: 'ar-demo-workflow', tools: DEMO_WORKFLOW_NO_TOOLS });

  // --- Staggered reveal: the workflow "builds itself" one box at a time ------
  const rfRef = useRef<ReactFlowInstance | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(
    () => new Set<string>(),
  );

  // Replaying the demo (Reset → re-create) restarts the build animation.
  useEffect(() => {
    if (!workflow.created) setRevealedIds(new Set<string>());
  }, [workflow.created]);

  // Reveal the next not-yet-shown node on a timer; the set also grows when
  // Scene 9 appends the dispute-call branch. Matching by id (not index) keeps
  // already-revealed nodes stable as new ones are added.
  useEffect(() => {
    if (!workflow.created) return;
    const next = nodes.find((n) => !revealedIds.has(n.id));
    if (!next) return;
    const delay = revealedIds.size === 0 ? 250 : 550;
    const timer = setTimeout(() => {
      setRevealedIds((prev) => new Set(prev).add(next.id));
    }, delay);
    return () => clearTimeout(timer);
  }, [workflow.created, nodes, revealedIds]);

  // When Scene 9 adds the branch, pan/zoom so the new nodes come into view.
  useEffect(() => {
    if (!workflow.amended) return;
    const timer = setTimeout(
      () => rfRef.current?.fitView({ padding: 0.2, duration: 500 }),
      100,
    );
    return () => clearTimeout(timer);
  }, [workflow.amended]);

  // --- Click-to-inspect detail pane ------------------------------------------
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  useEffect(() => {
    if (!workflow.created) setSelectedNodeId(null);
  }, [workflow.created]);

  const onNodeClick = useCallback((_: unknown, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);
  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);

  // All nodes stay mounted (so fitView frames the whole graph); visibility is
  // driven by `__revealed` so the view never jumps as boxes appear. `selected`
  // drives the product node's selected styling + our detail pane.
  const displayNodes = useMemo<Node[]>(
    () =>
      nodes.map((n) => ({
        ...n,
        selected: n.id === selectedNodeId,
        data: { ...n.data, __revealed: revealedIds.has(n.id) },
      })),
    [nodes, revealedIds, selectedNodeId],
  );
  const displayEdges = useMemo<Edge[]>(
    () =>
      edges.map((e) => ({
        ...e,
        style: {
          ...e.style,
          opacity:
            revealedIds.has(e.source) && revealedIds.has(e.target) ? 1 : 0,
          transition: 'opacity 400ms ease',
        },
      })),
    [edges, revealedIds],
  );

  // The build (and Scene 9's appended branch) is done once every node has been
  // revealed. When it completes, auto-select the statement node so its PDF lands
  // in the detail pane, then — after a beat — tell the scripted turn it may
  // stream Nami's "Done…" narration.
  const buildComplete =
    workflow.created && nodes.length > 0 && revealedIds.size >= nodes.length;
  useEffect(() => {
    if (!buildComplete) return;
    // Don't override a step the presenter clicked into during the build.
    setSelectedNodeId((cur) => cur ?? STATEMENT_NODE_ID);
    const timer = setTimeout(() => signalWorkflowBuildComplete(), 450);
    return () => clearTimeout(timer);
  }, [buildComplete]);

  const selectedNode = useMemo<DemoWorkflowNode | null>(
    () => workflow.nodes.find((n) => n.id === selectedNodeId) ?? null,
    [workflow.nodes, selectedNodeId],
  );

  if (!workflow.created) {
    return (
      <div className="flex h-full min-h-[60vh] flex-col items-center justify-center text-center">
        <SparkleIcon weight="fill" className="size-10 text-purple-400" />
        <h2 className="mt-3 text-lg font-semibold text-gray-900">
          <T context="AR demo workflow empty-state title">No workflow yet</T>
        </h2>
        <p className="mt-1 max-w-sm text-sm text-gray-500">
          <T context="AR demo workflow empty-state body">
            Ask Nami to create a weekly payment-reminder workflow and it will
            appear here.
          </T>
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100svh-8rem)] min-h-[32rem] flex-col overflow-hidden rounded-xl border border-gray-200 bg-background dark:border-gray-700">
      {/* Real product toolbar — pure chrome (buttons are no-ops). Watchers +
          version-picker props are omitted so it fires no RPCs. */}
      <WorkflowEditorHeader
        workflowName={workflow.name}
        workflowDescription={workflow.description}
        workflowTags={[]}
        onNameChange={noopString}
        onDescriptionChange={noopString}
        onTagsChange={noopTags}
        isCreateMode={false}
        isSaving={false}
        isDirty={false}
        hasTrigger
        hasScheduleTrigger
        onSave={noop}
        onReset={noop}
        onCancel={() => {
          void navigate({ to: paths.authenticated.accountsReceivable.summary });
        }}
        onAutoLayout={noop}
        onExportDownload={noop}
        onExportCopy={noop}
        onImport={noopFile}
        onUndo={noop}
        onRedo={noop}
        canUndo={false}
        canRedo={false}
        versionStatus="draft"
        versionNumber={1}
        onPublish={noop}
      />

      <div className="flex min-h-0 flex-1">
        {/* Canvas */}
        <ConnectionDragProvider>
          <div className="relative flex-1 overflow-hidden bg-slate-50 dark:bg-slate-900/50">
            {workflow.amended && (
              <div className="absolute left-4 top-4 z-10 inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700 shadow-sm">
                <SparkleIcon weight="fill" className="size-2.5" />
                <T context="AR demo workflow amended banner">
                  Updated with dispute-call branch
                </T>
              </div>
            )}
            <ReactFlow
              nodes={displayNodes}
              edges={displayEdges}
              nodeTypes={nodeTypes}
              onInit={(instance) => {
                rfRef.current = instance;
              }}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              proOptions={{ hideAttribution: true }}
              nodesConnectable={false}
              edgesFocusable={false}
            >
              <Background gap={16} color="#e2e8f0" />
              <Controls showInteractive={false}>
                <ControlButton
                  onClick={() => setIsMinimapOpen((v) => !v)}
                  title={
                    isMinimapOpen ? gt('Hide minimap') : gt('Show minimap')
                  }
                >
                  <MapTrifoldIcon />
                </ControlButton>
              </Controls>
              {isMinimapOpen && <MiniMap pannable zoomable />}
            </ReactFlow>
          </div>
        </ConnectionDragProvider>

        {/* Right panel — mirrors the real editor: the node catalog when nothing
            is selected, the step inspector (statement step renders its PDF) when
            a node is selected. */}
        <aside className="flex w-80 shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-background dark:border-gray-700">
          {selectedNode ? (
            <div className="overflow-y-auto p-4">
              <NodeInspector node={selectedNode} workflow={workflow} gt={gt} />
            </div>
          ) : (
            <NodeLibraryContent
              searchQuery={catalogSearch}
              onSearchChange={setCatalogSearch}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail pane content for the selected node.
// ---------------------------------------------------------------------------
function NodeInspector({
  node,
  workflow,
  gt,
}: {
  node: DemoWorkflowNode;
  workflow: DemoWorkflow;
  gt: ReturnType<typeof useGT>;
}) {
  const catStyle = getCategoryStyle(node.category);
  const isStatement = node.id === STATEMENT_NODE_ID;
  return (
    <div className="flex flex-col gap-3">
      <div>
        <span
          className={cn(
            'inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            catStyle.badge,
          )}
        >
          {node.category}
        </span>
        <h3 className="mt-2 text-sm font-semibold text-gray-900">
          {node.title}
        </h3>
        <p className="mt-1 text-xs leading-snug text-gray-500">
          {node.subtitle}
        </p>
      </div>

      {isStatement && (
        <div>
          <p className="text-xs text-gray-500">
            {workflow.statementHighlighted ? (
              <T context="AR demo workflow statement preview caption, highlighted">
                Overdue invoices are bolded and highlighted yellow in the
                attached PDF.
              </T>
            ) : (
              <T context="AR demo workflow statement preview caption, plain">
                A statement of each customer’s outstanding invoices, attached as
                a PDF.
              </T>
            )}
          </p>
          {/* Both statement layers are stacked; the highlighted one fades in
              over the plain one when scene 5b applies the styling. */}
          <div className="relative mt-3 aspect-[816/1056] overflow-hidden rounded-lg border border-gray-200">
            <img
              src={BRAND.plainStatementUrl}
              alt={gt('Statement of outstanding invoices')}
              className="absolute inset-0 h-full w-full"
            />
            <img
              src={BRAND.highlightedStatementUrl}
              alt={gt(
                'Statement with overdue invoices bolded and highlighted yellow',
              )}
              aria-hidden={!workflow.statementHighlighted}
              className={cn(
                'absolute inset-0 h-full w-full transition-opacity duration-700 ease-out',
                workflow.statementHighlighted ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
